import { validateBoardId } from "helpers/boards";
import { trimEnd, trimStart } from "helpers/utils";
import { createLogger } from "logging";

import { ParamsModel } from "./ParamsModel";

const logger = createLogger("getRouteParams");

export function getString(name: string, value: string | undefined, newValue: string | undefined): string | undefined {
  if (newValue) {
    logger.trace(`Detected ${name}:=${newValue}, (was: ${value})`);
    return newValue;
  }
  return value;
}

export function getNumber(name: string, value: number | undefined, candidate: string | undefined): number | undefined {
  const newValue = Number(candidate);
  if (newValue && !Number.isNaN(newValue)) {
    logger.trace(`Detected ${name}:=${newValue}, (was: ${value})`);
    return newValue;
  }
  return value;
}

export function getBoardId(boardId: string | undefined, candidate: string | undefined): string | undefined {
  const value = getString("boardId", boardId, candidate);
  if (value && validateBoardId(candidate).isValid) {
    logger.trace(`Detected boardId:=${candidate}, (was: ${boardId})`);
    return value;
  }
  return boardId;
}

export function getMeetingId(meetingId: number | undefined, candidate: string | undefined): number | undefined {
  return getNumber("meetingId", meetingId, candidate);
}

export function getAgendaItemId(agendaItemId: number | undefined, candidate: string | undefined): number | undefined {
  return getNumber("agendaItemId", agendaItemId, candidate);
}

export function getParticipantId(participantId: number | undefined, candidate: string | undefined): number | undefined {
  return getNumber("participantId", participantId, candidate);
}

export function getDocumentId(documentId: number | undefined, candidate: string | undefined): number | undefined {
  return getNumber("documentId", documentId, candidate);
}

export function getTaskId(taskId: number | undefined, candidate: string | undefined): number | undefined {
  return getNumber("taskId", taskId, candidate);
}

export function getRouteParams(pathname = window.location.pathname): ParamsModel {
  pathname = trimStart(pathname, "/");
  pathname = trimEnd(pathname, "/");

  const boardRegex = /^boards\/?$/;
  const boardIdRegex = /^(?<boardId>[^/]+)\/?$/;
  const boardSettingsRegex = /^(?<boardId>[^/]+)\/settings\/?$/;

  const meetingsRegex = /^(?<boardId>[^/]+)\/meetings$/;
  const meetingRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)$/;
  const agendaItemRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)\/agendaItems$/;
  const participantRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)\/participants$/;
  const documentRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)\/documents$/;
  const taskRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)\/tasks$/;
  const oneDriveSyncRegex = /^(?<boardId>[^/]+)\/meetings\/(?<meetingId>[^/]+)\/oneDrive$/;

  let boardId: string | undefined = undefined;
  let meetingId: number | undefined = undefined;
  let agendaItemId: number | undefined = undefined;
  let participantId: number | undefined = undefined;
  let documentId: number | undefined = undefined;
  let taskId: number | undefined = undefined;

  [
    boardIdRegex,
    meetingsRegex,
    meetingRegex,
    agendaItemRegex,
    participantRegex,
    documentRegex,
    taskRegex,
    oneDriveSyncRegex,
    boardSettingsRegex,
    boardRegex,
  ].forEach((regex) => {
    const groups = regex.exec(pathname)?.groups;
    logger.trace(`Exec`, regex, pathname, groups);

    boardId = getBoardId(boardId, groups?.boardId);
    meetingId = getMeetingId(meetingId, groups?.meetingId);
    participantId = getParticipantId(participantId, groups?.participantId);
    agendaItemId = getAgendaItemId(agendaItemId, groups?.agendaItemId);
    documentId = getDocumentId(documentId, groups?.documentId);
    taskId = getTaskId(taskId, groups?.taskId);
  });

  return {
    boardId,
    meetingId,
    agendaItemId,
    participantId,
    documentId,
    taskId,
    isBoards: boardRegex.test(pathname),
    isMeetings: meetingsRegex.test(pathname),
    isMeeting: meetingRegex.test(pathname),
    isMeetingParticipants: participantRegex.test(pathname),
    isMeetingAgendaItems: agendaItemRegex.test(pathname),
    isMeetingDocuments: documentRegex.test(pathname),
    isMeetingOnedriveSync: oneDriveSyncRegex.test(pathname),
    isMeetingTasks: taskRegex.test(pathname),
  };
}
